import { defineStore } from 'pinia';
import { ref } from 'vue';

import { fetchGeneralLists, type GeneralLists } from '@/api/general';
import { fetchMoreEvents } from '@/api/moreEvents';
import type { Customer, EventItem, FrontpageSegment } from '@/types';

export const useGeneralStore = defineStore('general', () => {
  const general = ref<GeneralLists | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const generalRev = ref<number | undefined>(undefined);
  const moreEventsRev = ref<number | undefined>(undefined);
  const moreEventsCached = ref<EventItem[]>([]);


  async function fetchGeneral() {
    loading.value = true;
    error.value = null;
    try {
      general.value = await fetchGeneralLists();
      generalRev.value = general.value.rev ?? undefined;
      // The inner tryblock does not obstruct fetchGeneral. It will finish when fetchMoreEvents responds.
      try {
        if(generalRev.value && (!moreEventsRev.value || moreEventsRev.value < generalRev.value)){
          const { moreEvents, revision } = await fetchMoreEvents(generalRev.value);
          if (general.value) {
            // Append more events to existing events
            // console.log("Fetched more events:", moreEvents);
            moreEventsRev.value = revision;
            moreEventsCached.value = moreEvents;
            general.value.moreEvents = moreEvents;
            // console.log("Updated moreEvents in generalstore:", general.value.moreEvents);
          }
        }
        else if(moreEventsCached.value.length > 0 && general.value){
          general.value.moreEvents = moreEventsCached.value;
          // console.log("Used cached more events:", general.value.moreEvents);
        }
        else {
          console.log("No need to fetch more events.");
        }
      } catch (e) {
        console.error("Failed to fetch more events:", e);
      }
    } catch (e: any) {
      error.value = e.message || 'Unknown error';
      general.value = { events: [], moreEvents: [], venues: [], categories: [], promoted: [], customer: {} as Customer, frontpage2_segments: [], languages: [], short_urls: {}, rev: undefined };
    } finally {
      loading.value = false;
    }
    console.log("general", general.value);
  }

  // Example getters for common fields
  const events = () => {
    const baseEvents = general.value?.events ?? [] as EventItem[];
    const additionalEvents = general.value?.moreEvents ?? [] as EventItem[];
    return baseEvents.concat(additionalEvents);
  };
  const categories = () => general.value?.categories ?? [];
  const venues = () => general.value?.venues ?? [];
  const customer = () => general.value?.customer ?? {} as Customer;
  const promoted = () => general.value?.promoted ?? [];
  const frontpage2_segments = () => general.value?.frontpage2_segments ?? [] as FrontpageSegment[];
  const languages = () => general.value?.languages ?? [];
  const short_urls = () => general.value?.short_urls ?? [];

  return {
    general,
    loading,
    error,
    fetchGeneral,
    events,
    categories,
    venues,
    customer,
    promoted,
    frontpage2_segments,
    languages,
    short_urls
  };
});